// L'image d'aperçu d'un club quand on partage un de ses liens (WhatsApp, Messages, Facebook…) : 1200 × 630,
// variante 1 validée par Idries (fond à la couleur du club, logo dans un rond blanc, « L'app du club », son nom,
// « Planning, convocations, matchs et vie du club. », « avec Arceau »). Dessinée ici, dans l'app, comme l'icône de
// l'écran d'accueil (D-102) : à la première ouverture d'un admin après un changement, ou quand le bureau enregistre
// ses couleurs et son logo ; la base la garde (clubs.og_image) et la fonction club-app la sert sur /og.jpg.
import { isHexColor, monogram, palette } from '../domain/brand';
import { updateClubBrand, type ClubBrand } from '../lib/api';

const W = 1200, H = 630;
const DEFAULT = '#E8590C', NIGHT = '#17191C';
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
type OgSource = Pick<ClubBrand, 'name' | 'short_name' | 'logo_url' | 'primary_color' | 'secondary_color'>;

/** Empreinte de ce qui fait l'image : on ne la redessine que si le logo, les couleurs ou le nom changent. */
export function ogImageKey(b: OgSource): string {
  const s = `og1|${b.logo_url ?? ''}|${b.primary_color ?? ''}|${b.secondary_color ?? ''}|${b.short_name ?? ''}|${b.name}`;
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; }
  return h.toString(16).padStart(8, '0');
}

/** Le nom sur une ou deux lignes, en Anton, à la plus grande taille qui tient (92 px au plus). */
function fitTitle(g: CanvasRenderingContext2D, name: string, width: number): { lines: string[]; px: number } {
  const words = name.toUpperCase().split(/\s+/).filter(Boolean);
  for (let px = 92; px >= 44; px -= 4) {
    g.font = `400 ${px}px Anton, Impact, "Arial Narrow", sans-serif`;
    if (g.measureText(words.join(' ')).width <= width) return { lines: [words.join(' ')], px };
    // Deux lignes : la coupure la plus équilibrée qui tient (« BASKET CLUB / DE RIVEBELLE »).
    let best: string[] | null = null, bestW = Infinity;
    for (let cut = 1; cut < words.length; cut++) {
      const a = words.slice(0, cut).join(' '), b = words.slice(cut).join(' ');
      const w = Math.max(g.measureText(a).width, g.measureText(b).width);
      if (w <= width && w < bestW) { best = [a, b]; bestW = w; }
    }
    if (best) return { lines: best, px };
  }
  return { lines: [words.join(' ')], px: 44 };
}

/** Texte espacé (lettrage des sur-titres) : letterSpacing n'existe pas partout. */
function spaced(g: CanvasRenderingContext2D, text: string, x: number, y: number, gap: number, align: 'left' | 'right' = 'left'): void {
  const total = [...text].reduce((w, ch) => w + g.measureText(ch).width + gap, -gap);
  let cx = align === 'right' ? x - total : x;
  for (const ch of text) { g.fillText(ch, cx, y); cx += g.measureText(ch).width + gap; }
}

function ball(g: CanvasRenderingContext2D, cx: number, cy: number, r: number): void {
  g.save();
  g.fillStyle = '#E8590C'; g.strokeStyle = NIGHT; g.lineWidth = Math.max(1.5, r / 7);
  g.beginPath(); g.arc(cx, cy, r, 0, Math.PI * 2); g.fill(); g.stroke();
  g.beginPath(); g.moveTo(cx - r, cy); g.lineTo(cx + r, cy); g.moveTo(cx, cy - r); g.lineTo(cx, cy + r);
  g.moveTo(cx - r * 0.62, cy - r * 0.62); g.quadraticCurveTo(cx - r * 0.25, cy, cx - r * 0.62, cy + r * 0.62);
  g.moveTo(cx + r * 0.62, cy - r * 0.62); g.quadraticCurveTo(cx + r * 0.25, cy, cx + r * 0.62, cy + r * 0.62);
  g.stroke(); g.restore();
}

/** L'image d'aperçu du club, en JPEG (data URL). */
export async function drawOgImage(b: OgSource): Promise<string> {
  try { await Promise.all(['400 92px Anton', '400 28px "Inter Variable"', '700 20px "JetBrains Mono"'].map(f => document.fonts.load(f))); } catch { /* polices de repli */ }
  const canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = H;
  const g = canvas.getContext('2d')!;
  const color = isHexColor(b.primary_color) ? b.primary_color! : DEFAULT;
  const stripe = isHexColor(b.secondary_color) ? b.secondary_color! : NIGHT;
  const ink = palette(color).light.ink;
  g.fillStyle = color; g.fillRect(0, 0, W, H);
  g.fillStyle = stripe; g.fillRect(0, H - 16, W, 16);

  // Le logo dans un rond blanc (ses initiales sinon), une ombre douce dessous.
  const cx = 230, cy = 315, r = 150;
  g.save(); g.shadowColor = 'rgba(0,0,0,.25)'; g.shadowBlur = 50; g.shadowOffsetY = 20;
  g.fillStyle = '#FFFFFF'; g.beginPath(); g.arc(cx, cy, r, 0, Math.PI * 2); g.fill(); g.restore();
  if (b.logo_url) {
    const img = new Image(); img.src = b.logo_url;
    await img.decode();
    const box = 250, k = Math.min(box / img.naturalWidth, box / img.naturalHeight);
    const w = img.naturalWidth * k, h = img.naturalHeight * k;
    g.save(); g.beginPath(); g.arc(cx, cy, r - 6, 0, Math.PI * 2); g.clip();
    g.imageSmoothingQuality = 'high'; g.drawImage(img, cx - w / 2, cy - h / 2, w, h); g.restore();
  } else {
    const text = monogram(b.name, b.short_name);
    g.fillStyle = color; g.textAlign = 'center'; g.textBaseline = 'middle';
    g.font = `400 ${text.length <= 2 ? 130 : text.length === 3 ? 104 : 84}px Anton, Impact, sans-serif`;
    g.fillText(text, cx, cy + 6); g.textAlign = 'left';
  }

  // Le texte, centré verticalement à droite du logo.
  const x = 440, width = W - x - 70;
  const t = fitTitle(g, b.name, width);
  const kickH = 24, gap1 = 18, lineH = t.px * 0.98, gap2 = 22, subH = 34;
  const block = kickH + gap1 + lineH * t.lines.length + gap2 + subH;
  let y = (H - block) / 2;
  g.fillStyle = ink; g.textBaseline = 'top';
  g.globalAlpha = 0.85; g.font = '700 20px "JetBrains Mono", ui-monospace, monospace';
  spaced(g, 'L’APP DU CLUB', x, y, 3); g.globalAlpha = 1;
  y += kickH + gap1;
  g.font = `400 ${t.px}px Anton, Impact, "Arial Narrow", sans-serif`;
  for (const line of t.lines) { g.fillText(line, x, y); y += lineH; }
  y += gap2;
  g.globalAlpha = 0.92; g.font = '400 28px "Inter Variable", Inter, sans-serif';
  g.fillText('Planning, convocations, matchs et vie du club.', x, y, width); g.globalAlpha = 1;

  // « avec Arceau », en bas à droite.
  g.globalAlpha = 0.8; g.font = '700 15px "JetBrains Mono", ui-monospace, monospace'; g.textBaseline = 'middle';
  spaced(g, 'AVEC ARCEAU', W - 70, H - 56, 2, 'right');
  const tw = [...'AVEC ARCEAU'].reduce((w, ch) => w + g.measureText(ch).width + 2, -2);
  g.globalAlpha = 1; ball(g, W - 70 - tw - 18, H - 56, 10);
  return canvas.toDataURL('image/jpeg', 0.86);
}

/** L'image à jour pour un club, à enregistrer avec ses couleurs (null : rien à changer). */
export async function ogImagePatch(b: OgSource & { og_image_for?: string | null }): Promise<{ og_image: string; og_image_for: string } | null> {
  const key = ogImageKey(b);
  if (b.og_image_for === key) return null;
  return { og_image: await drawOgImage(b), og_image_for: key };
}

/** Première ouverture d'un admin après un changement (ou d'un club d'avant les aperçus) : l'image se fait toute seule. */
export async function ensureOgImage(b: ClubBrand | null, isAdmin: boolean): Promise<void> {
  if (!b || !isAdmin || !UUID.test(b.id)) return;
  try {
    const patch = await ogImagePatch(b);
    if (patch) { await updateClubBrand(b.id, patch); b.og_image_for = patch.og_image_for; }
  } catch { /* hors ligne, image illisible : on réessaiera à la prochaine ouverture */ }
}
